// EXPORTS: ITrainingPlanDay, ITrainingPlan, generateTrainingPlan, matchCourseSystem, getCarbDayFromTrainingDay, HOME_EXERCISE_GAP_LIST
// 训练方案自动生成器：根据用户档案智能分流，生成持久化周训练计划
// 智能分流规则：经验 × 频率 × 场地 × 目标 → 课程体系 + 周编排 + 动作版本

import type { DietPlanTemplate } from './diet-plan-templates';
import { MOCK_COURSES, type CourseSystem, type ICourse } from './courses';
import { MOCK_EXERCISES } from './exercises';
import { getHomeTrainingDay, getTrainingExperienceSettings, getTrainingSessionMinutes } from './training-environment';
import { selectGymTrainingDay } from './training-course-selection';
export { HOME_EXERCISE_GAP_LIST } from './training-environment';
import { INJURY_AVOID_MAP, type InjuryPart } from './injury-recovery-plan';

export type TrainingDayType = 'strength' | 'hiit' | 'cardio' | 'active-rest' | 'rest';

export interface ITrainingPlanDay {
  /** 周几 1-7，1=周一 */
  dayOfWeek: number;
  /** 日类型 */
  type: TrainingDayType;
  /** 显示名称 */
  label: string;
  /** 副标题 / 部位或项目说明 */
  subtitle: string;
  /** 预计时长（分钟） */
  durationMin: number;
  /** 强度提示：低/中/高 */
  intensity?: '低' | '中' | '高';
  /** 说明文字 */
  note?: string;
  // ===== 力量日专属字段 =====
  /** 关联课程体系 ID */
  courseSystem?: CourseSystem;
  /** 关联课程 ID */
  courseId?: string;
  /** 关联课程训练日 ID */
  courseDayId?: string;
  /** 动作 ID 列表（主项） */
  exerciseIds?: string[];
  /** 热身动作 ID */
  warmupIds?: string[];
  /** 拉伸动作 ID */
  stretchIds?: string[];
  // ===== 有氧日专属字段 =====
  /** 有氧项目描述 */
  cardioItem?: string;
}

export interface ITrainingPlan {
  id: string;
  name: string;
  /** 对应饮食方案模板 */
  dietTemplate: DietPlanTemplate;
  /** 课程体系 */
  courseSystem: CourseSystem;
  /** 训练目标 */
  goal: 'fat-loss' | 'muscle-gain' | 'shape';
  /** 每周训练小时数 */
  weeklyHours: number;
  /** 单次训练时长（分钟），旧计划可能未保存 */
  sessionMinutes?: number;
  /** 训练场地 */
  trainingPlace: 'gym' | 'home';
  /** 训练经验（月） */
  trainingMonths: number;
  /** 每周训练频率 */
  trainingFrequency: '1-2' | '3-4' | '5-6';
  /** 方案开始执行日期（时间戳），第1天 = 当天 */
  startDate: number;
  /** 新方案按实际星期执行，旧方案保留历史循环 */
  scheduleMode?: 'calendar';
  /** 7 天周计划 */
  weekDays: ITrainingPlanDay[];
  /** 生成时间 */
  createdAt: number;
}

interface GenerateInput {
  goal: 'fat-loss' | 'muscle-gain' | 'shape';
  dietTemplate: DietPlanTemplate;
  trainingPlace: 'gym' | 'home';
  trainingMonths: number;
  trainingFrequency: '1-2' | '3-4' | '5-6';
  weeklyHours: number;
  sessionMinutes?: number;
  gender: 'male' | 'female';
  /** 用户勾选的每周力量训练日，1=周一 ... 7=周日；空则按频率默认 */
  strengthDays?: number[];
  /** 计划开始日期时间戳（ms，0 点）；不传默认今天 */
  startDate?: number;
  /** 伤病部位（英文 key），生成计划时规避涉及该部位的动作 */
  injuryParts?: InjuryPart[];
}

// ========== 经验档位判定 ==========
function experienceTier(months: number): 'beginner' | 'intermediate' | 'advanced' {
  if (months < 6) return 'beginner';      // 新手 < 6月
  if (months < 24) return 'intermediate'; // 老手 6月-2年
  return 'advanced';                       // 资深 > 2年
}

/**
 * 课程体系智能匹配规则（经验 × 频率 × 场地 × 目标 → 体系）
 *
 * 表格：
 * 经验    | 1-2次/周          | 3-4次/周              | 5-6次/周
 * 新手    | 私教跟练单部位    | 三分化推拉蹲          | 三分化×2循环
 * 老手    | 私教跟练按短板    | 四分化胸背腿肩臂      | 四分化5天+有氧穿插
 * 资深    | 精讲单日          | 凯圣王三分化合集        | 凯圣王三分化×2 或 四分化+晋升突破进阶
 *
 * 居家：全部映射到居家训练合集（按频率分全身/上下肢）
 */
export function matchCourseSystem(input: GenerateInput): CourseSystem {
  const { goal, trainingPlace, trainingMonths, trainingFrequency } = input;

  // 居家 → 居家训练合集（按频率自动切换全身/上下肢分化）
  if (trainingPlace === 'home') return 'home';

  // 塑形目标经验分档与 applyGoalFromAssessment 对齐：<12月=新手、12-36月=老手、>36月=资深
  // 增肌/减脂：<6月=新手、6-24月=老手、>24月=资深
  const tier = goal === 'shape'
    ? (trainingMonths < 12 ? 'beginner' : trainingMonths < 36 ? 'intermediate' : 'advanced')
    : experienceTier(trainingMonths);

  // 减脂：以三分化为基准
  if (goal === 'fat-loss') {
    if (tier === 'beginner') {
      if (trainingFrequency === '1-2') return 'personal';
      if (trainingFrequency === '3-4') return 'three-way';
      return 'three-way';  // 5-6次：三分化×2循环
    }
    if (tier === 'intermediate') {
      if (trainingFrequency === '1-2') return 'personal';
      if (trainingFrequency === '3-4') return 'four-way';
      return 'four-way';   // 5-6次：四分化+有氧
    }
    // advanced
    if (trainingFrequency === '1-2') return 'ksw-collection';
    if (trainingFrequency === '3-4') return 'ksw-collection';
    return 'ksw-collection';
  }

  // 增肌：以增肌方案为基准
  if (goal === 'muscle-gain') {
    if (tier === 'beginner') {
      if (trainingFrequency === '1-2') return 'personal';
      if (trainingFrequency === '3-4') return 'three-way';
      return 'three-way';
    }
    if (tier === 'intermediate') {
      if (trainingFrequency === '1-2') return 'personal';
      if (trainingFrequency === '3-4') return 'four-way';
      return 'four-way';
    }
    // advanced
    if (trainingFrequency === '1-2') return 'ksw-collection';
    if (trainingFrequency === '3-4') return 'ksw-collection';
    return 'ksw-collection';
  }

  // 塑形：以塑形方案为基准
  if (tier === 'beginner') {
    if (trainingFrequency === '1-2') return 'personal';
    if (trainingFrequency === '3-4') return 'three-way';
    return 'three-way';
  }
  if (tier === 'intermediate') {
    if (trainingFrequency === '1-2') return 'personal';
    if (trainingFrequency === '3-4') return 'four-way';
    return 'four-way';
  }
  // advanced shape
  if (trainingFrequency === '1-2') return 'ksw-collection';
  if (trainingFrequency === '3-4') return 'ksw-collection';
  return 'ksw-collection';
}

// ========== 课程查找 ==========

function findCourseBySystem(system: CourseSystem): ICourse {
  return MOCK_COURSES.find(c => c.system === system) || MOCK_COURSES[0];
}

// ========== 周编排生成（按频率+目标+场地） ==========

interface WeekBuildInput {
  system: CourseSystem;
  course: ICourse;
  frequency: '1-2' | '3-4' | '5-6';
  goal: 'fat-loss' | 'muscle-gain' | 'shape';
  trainingPlace: 'gym' | 'home';
  trainingMonths: number;
}

/**
 * 按频率+目标生成周计划
 * 力量日绑定具体课程训练日；有氧/休息日照目标配
 */
function buildWeekDays(input: WeekBuildInput): Omit<ITrainingPlanDay, 'courseId' | 'courseDayId' | 'exerciseIds' | 'warmupIds' | 'stretchIds'>[] {
  const { system, frequency, goal, trainingPlace } = input;

  // 居家模式：按频率分档（全身/上下肢分化）
  if (trainingPlace === 'home') {
    return buildHomeWeek(frequency, goal);
  }

  // 健身房：按体系+频率+目标
  if (system === 'personal') {
    return buildPersonalWeek(frequency, goal);
  }
  if (system === 'three-way') {
    return buildThreeWayWeek(frequency, goal);
  }
  if (system === 'four-way') {
    return buildFourWayWeek(frequency, goal);
  }
  if (system === 'ksw-collection') {
    return buildKaishengWeek(frequency, goal);
  }
  // 兜底
  return buildThreeWayWeek('3-4', goal);
}

function buildPersonalWeek(freq: '1-2' | '3-4' | '5-6', goal: 'fat-loss' | 'muscle-gain' | 'shape') {
  // 私教跟练：每次一个部位，频率低时单部位精练
  if (freq === '1-2') {
    return [
      { dayOfWeek: 1, type: 'strength' as const, label: '胸部训练', subtitle: '胸 · 私教跟练', durationMin: 65, intensity: '高' as const, note: '单部位精练' },
      { dayOfWeek: 2, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '低'), durationMin: cardioMinByGoal(goal, 'short'), intensity: '低' as const },
      { dayOfWeek: 3, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 4, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 5, type: 'strength' as const, label: '背部训练', subtitle: '背 · 私教跟练', durationMin: 70, intensity: '高' as const, note: '单部位精练' },
      { dayOfWeek: 6, type: 'active-rest' as const, label: '主动恢复', subtitle: '散步 / 拉伸', durationMin: 30, intensity: '低' as const },
      { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
    ];
  }
  if (freq === '3-4') {
    return [
      { dayOfWeek: 1, type: 'strength' as const, label: '胸部训练', subtitle: '胸 · 私教跟练', durationMin: 65, intensity: '高' as const, note: '单部位精练' },
      { dayOfWeek: 2, type: 'strength' as const, label: '背部训练', subtitle: '背 · 私教跟练', durationMin: 70, intensity: '高' as const, note: '单部位精练' },
      { dayOfWeek: 3, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '中'), durationMin: cardioMinByGoal(goal, 'mid'), intensity: '中' as const },
      { dayOfWeek: 4, type: 'strength' as const, label: '腿部训练', subtitle: '腿 · 私教跟练', durationMin: 75, intensity: '高' as const, note: '单部位精练' },
      { dayOfWeek: 5, type: 'strength' as const, label: '肩臂训练', subtitle: '肩+臂 · 私教跟练', durationMin: 60, intensity: '中' as const },
      { dayOfWeek: 6, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '低'), durationMin: cardioMinByGoal(goal, 'short'), intensity: '低' as const },
      { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
    ];
  }
  // 5-6次：私教全身循环
  return [
    { dayOfWeek: 1, type: 'strength' as const, label: '胸部训练', subtitle: '胸 · 私教跟练', durationMin: 65, intensity: '高' as const },
    { dayOfWeek: 2, type: 'strength' as const, label: '背部训练', subtitle: '背 · 私教跟练', durationMin: 70, intensity: '高' as const },
    { dayOfWeek: 3, type: 'strength' as const, label: '腿部训练', subtitle: '腿 · 私教跟练', durationMin: 75, intensity: '高' as const },
    { dayOfWeek: 4, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '中'), durationMin: cardioMinByGoal(goal, 'mid'), intensity: '中' as const },
    { dayOfWeek: 5, type: 'strength' as const, label: '肩部训练', subtitle: '肩 · 私教跟练', durationMin: 55, intensity: '中' as const },
    { dayOfWeek: 6, type: 'strength' as const, label: '手臂+核心', subtitle: '臂+腹 · 私教跟练', durationMin: 50, intensity: '中' as const },
    { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
  ];
}

function buildThreeWayWeek(freq: '1-2' | '3-4' | '5-6', goal: 'fat-loss' | 'muscle-gain' | 'shape') {
  const shapeNote = goal === 'shape' ? '含体态专项' : undefined;
  if (freq === '1-2') {
    return [
      { dayOfWeek: 1, type: 'strength' as const, label: '推日', subtitle: '胸 · 肩 · 三头', durationMin: 70, intensity: '高' as const, note: shapeNote },
      { dayOfWeek: 2, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '低'), durationMin: cardioMinByGoal(goal, 'short'), intensity: '低' as const },
      { dayOfWeek: 3, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 4, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 5, type: 'strength' as const, label: '全身日', subtitle: '推拉腿复合', durationMin: 75, intensity: '高' as const, note: shapeNote },
      { dayOfWeek: 6, type: 'active-rest' as const, label: '主动恢复', subtitle: '散步 / 拉伸', durationMin: 30, intensity: '低' as const },
      { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
    ];
  }
  if (freq === '3-4') {
    return [
      { dayOfWeek: 1, type: 'strength' as const, label: '推日', subtitle: '胸 · 肩 · 三头', durationMin: 75, intensity: '高' as const, note: shapeNote },
      { dayOfWeek: 2, type: 'strength' as const, label: '拉日', subtitle: '背 · 二头', durationMin: 75, intensity: '高' as const },
      { dayOfWeek: 3, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '中'), durationMin: cardioMinByGoal(goal, 'mid'), intensity: '中' as const },
      { dayOfWeek: 4, type: 'strength' as const, label: '腿日', subtitle: '腿 · 核心', durationMin: 80, intensity: '高' as const },
      { dayOfWeek: 5, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 6, type: goal === 'fat-loss' ? 'cardio' as const : 'active-rest' as const,
        label: goal === 'fat-loss' ? '有氧' : '主动恢复',
        subtitle: cardioByGoal(goal, '低'),
        durationMin: goal === 'fat-loss' ? 40 : 30,
        intensity: '低' as const },
      { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
    ];
  }
  // 5-6次：三分化×2循环
  return [
    { dayOfWeek: 1, type: 'strength' as const, label: '推日', subtitle: '胸 · 肩 · 三头', durationMin: 75, intensity: '高' as const, note: shapeNote },
    { dayOfWeek: 2, type: 'strength' as const, label: '拉日', subtitle: '背 · 二头', durationMin: 75, intensity: '高' as const },
    { dayOfWeek: 3, type: 'strength' as const, label: '腿日', subtitle: '腿 · 核心', durationMin: 80, intensity: '高' as const },
    { dayOfWeek: 4, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '中'), durationMin: cardioMinByGoal(goal, 'mid'), intensity: '中' as const },
    { dayOfWeek: 5, type: 'strength' as const, label: '推日（循环2）', subtitle: '胸 · 肩 · 三头', durationMin: 70, intensity: '高' as const, note: shapeNote ? shapeNote + ' · 第二循环' : '第二循环' },
    { dayOfWeek: 6, type: 'strength' as const, label: '拉日（循环2）', subtitle: '背 · 二头', durationMin: 70, intensity: '高' as const, note: '第二循环' },
    { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
  ];
}

function buildFourWayWeek(freq: '1-2' | '3-4' | '5-6', goal: 'fat-loss' | 'muscle-gain' | 'shape') {
  const shapeNote = goal === 'shape' ? '含体态专项' : undefined;
  if (freq === '1-2') {
    return [
      { dayOfWeek: 1, type: 'strength' as const, label: '胸+三头', subtitle: '胸日 · 带三头', durationMin: 70, intensity: '高' as const, note: shapeNote },
      { dayOfWeek: 2, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '低'), durationMin: cardioMinByGoal(goal, 'short'), intensity: '低' as const },
      { dayOfWeek: 3, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 4, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 5, type: 'strength' as const, label: '背+二头', subtitle: '背日 · 带二头', durationMin: 75, intensity: '高' as const },
      { dayOfWeek: 6, type: 'active-rest' as const, label: '主动恢复', subtitle: '散步 / 拉伸', durationMin: 30, intensity: '低' as const },
      { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
    ];
  }
  if (freq === '3-4') {
    return [
      { dayOfWeek: 1, type: 'strength' as const, label: '胸+肩+三头', subtitle: '推类日', durationMin: 75, intensity: '高' as const, note: shapeNote },
      { dayOfWeek: 2, type: 'strength' as const, label: '背+二头', subtitle: '拉类日', durationMin: 75, intensity: '高' as const },
      { dayOfWeek: 3, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '中'), durationMin: cardioMinByGoal(goal, 'mid'), intensity: '中' as const },
      { dayOfWeek: 4, type: 'strength' as const, label: '腿日', subtitle: '腿 · 臀', durationMin: 80, intensity: '高' as const },
      { dayOfWeek: 5, type: 'strength' as const, label: '肩+手臂', subtitle: '肩 · 臂强化', durationMin: 65, intensity: '中' as const },
      { dayOfWeek: 6, type: goal === 'fat-loss' ? 'cardio' as const : 'active-rest' as const,
        label: goal === 'fat-loss' ? '有氧' : '主动恢复',
        subtitle: cardioByGoal(goal, '低'),
        durationMin: goal === 'fat-loss' ? 40 : 30,
        intensity: '低' as const },
      { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
    ];
  }
  // 5-6次：四分化5天 + 有氧穿插
  return [
    { dayOfWeek: 1, type: 'strength' as const, label: '胸日', subtitle: '胸 + 三头', durationMin: 75, intensity: '高' as const, note: shapeNote },
    { dayOfWeek: 2, type: 'strength' as const, label: '背日', subtitle: '背 + 二头', durationMin: 75, intensity: '高' as const },
    { dayOfWeek: 3, type: 'strength' as const, label: '肩日', subtitle: '肩 + 核心', durationMin: 70, intensity: '高' as const },
    { dayOfWeek: 4, type: 'strength' as const, label: '腿日', subtitle: '腿 + 臀', durationMin: 80, intensity: '高' as const },
    { dayOfWeek: 5, type: 'strength' as const, label: '手臂+有氧', subtitle: '二头+三头 + 收尾有氧', durationMin: 70, intensity: '中' as const },
    { dayOfWeek: 6, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '中'), durationMin: cardioMinByGoal(goal, 'mid'), intensity: '中' as const },
    { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
  ];
}

function buildKaishengWeek(freq: '1-2' | '3-4' | '5-6', goal: 'fat-loss' | 'muscle-gain' | 'shape') {
  // 精讲：高质量动作，次数少但强度高
  const premium = '精讲';
  if (freq === '1-2') {
    return [
      { dayOfWeek: 1, type: 'strength' as const, label: `推日·${premium}`, subtitle: '胸 · 肩 · 三头 精讲', durationMin: 80, intensity: '高' as const, note: '动作细节打磨' },
      { dayOfWeek: 2, type: 'cardio' as const, label: '低强度有氧', subtitle: cardioByGoal(goal, '低'), durationMin: 30, intensity: '低' as const },
      { dayOfWeek: 3, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 4, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 5, type: 'strength' as const, label: `拉日·${premium}`, subtitle: '背 · 二头 精讲', durationMin: 75, intensity: '高' as const, note: '动作细节打磨' },
      { dayOfWeek: 6, type: 'active-rest' as const, label: '主动恢复', subtitle: '散步 / 拉伸', durationMin: 30, intensity: '低' as const },
      { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
    ];
  }
  if (freq === '3-4') {
    return [
      { dayOfWeek: 1, type: 'strength' as const, label: `推日·${premium}`, subtitle: '胸 · 肩 · 三头 精讲', durationMin: 80, intensity: '高' as const },
      { dayOfWeek: 2, type: 'strength' as const, label: `拉日·${premium}`, subtitle: '背 · 二头 精讲', durationMin: 75, intensity: '高' as const },
      { dayOfWeek: 3, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '中'), durationMin: cardioMinByGoal(goal, 'mid'), intensity: '中' as const },
      { dayOfWeek: 4, type: 'strength' as const, label: `蹲日·${premium}`, subtitle: '腿 · 核心 精讲', durationMin: 80, intensity: '高' as const },
      { dayOfWeek: 5, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 6, type: goal === 'fat-loss' ? 'cardio' as const : 'active-rest' as const,
        label: goal === 'fat-loss' ? '有氧' : '主动恢复',
        subtitle: cardioByGoal(goal, '低'),
        durationMin: goal === 'fat-loss' ? 45 : 30,
        intensity: '低' as const },
      { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
    ];
  }
  // 5-6次：精讲三分化×2 或 四分化+进阶
  return [
    { dayOfWeek: 1, type: 'strength' as const, label: `推日·${premium}`, subtitle: '胸 · 肩 · 三头', durationMin: 80, intensity: '高' as const },
    { dayOfWeek: 2, type: 'strength' as const, label: `拉日·${premium}`, subtitle: '背 · 二头', durationMin: 75, intensity: '高' as const },
    { dayOfWeek: 3, type: 'strength' as const, label: `蹲日·${premium}`, subtitle: '腿 · 核心', durationMin: 80, intensity: '高' as const },
    { dayOfWeek: 4, type: 'cardio' as const, label: '有氧', subtitle: cardioByGoal(goal, '中'), durationMin: cardioMinByGoal(goal, 'mid'), intensity: '中' as const },
    { dayOfWeek: 5, type: 'strength' as const, label: '上肢强化', subtitle: '薄弱部位精讲打磨', durationMin: 70, intensity: '中' as const, note: '针对性进阶' },
    { dayOfWeek: 6, type: 'strength' as const, label: '核心+功能', subtitle: '核心稳定性 + 体态', durationMin: 50, intensity: '中' as const },
    { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
  ];
}

function buildHomeWeek(freq: '1-2' | '3-4' | '5-6', goal: 'fat-loss' | 'muscle-gain' | 'shape') {
  if (freq === '1-2') {
    return [
      { dayOfWeek: 1, type: 'strength' as const, label: '全身训练 A', subtitle: '居家自重+弹力带', durationMin: 35, intensity: '中' as const, note: '居家版' },
      { dayOfWeek: 2, type: 'cardio' as const, label: '居家有氧', subtitle: cardioByGoal(goal, '低'), durationMin: 25, intensity: '低' as const },
      { dayOfWeek: 3, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 4, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 5, type: 'strength' as const, label: '全身训练 B', subtitle: '居家自重+弹力带', durationMin: 35, intensity: '中' as const, note: '居家版' },
      { dayOfWeek: 6, type: 'active-rest' as const, label: '主动恢复', subtitle: '散步 / 拉伸', durationMin: 25, intensity: '低' as const },
      { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
    ];
  }
  if (freq === '3-4') {
    return [
      { dayOfWeek: 1, type: 'strength' as const, label: '上肢训练', subtitle: '胸+背+肩+臂', durationMin: 40, intensity: '中' as const, note: '居家版' },
      { dayOfWeek: 2, type: 'strength' as const, label: '下肢训练', subtitle: '腿+臀+核心', durationMin: 40, intensity: '中' as const, note: '居家版' },
      { dayOfWeek: 3, type: 'cardio' as const, label: '居家有氧', subtitle: cardioByGoal(goal, '中'), durationMin: 30, intensity: '中' as const },
      { dayOfWeek: 4, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
      { dayOfWeek: 5, type: 'strength' as const, label: '全身训练', subtitle: '复合动作为主', durationMin: 35, intensity: '中' as const, note: '居家版' },
      { dayOfWeek: 6, type: goal === 'fat-loss' ? 'cardio' as const : 'active-rest' as const,
        label: goal === 'fat-loss' ? '居家有氧' : '主动恢复',
        subtitle: cardioByGoal(goal, '低'),
        durationMin: goal === 'fat-loss' ? 30 : 25,
        intensity: '低' as const },
      { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
    ];
  }
  // 5-6次居家
  return [
    { dayOfWeek: 1, type: 'strength' as const, label: '推日', subtitle: '胸+肩+三头 居家版', durationMin: 40, intensity: '中' as const, note: '居家版' },
    { dayOfWeek: 2, type: 'strength' as const, label: '拉日', subtitle: '背+二头 居家版', durationMin: 40, intensity: '中' as const, note: '居家版' },
    { dayOfWeek: 3, type: 'strength' as const, label: '腿日', subtitle: '腿+臀 居家版', durationMin: 40, intensity: '中' as const, note: '居家版' },
    { dayOfWeek: 4, type: 'cardio' as const, label: '居家有氧', subtitle: cardioByGoal(goal, '中'), durationMin: 30, intensity: '中' as const },
    { dayOfWeek: 5, type: 'strength' as const, label: '上肢循环', subtitle: '推拉超级组', durationMin: 35, intensity: '中' as const, note: '居家版' },
    { dayOfWeek: 6, type: 'strength' as const, label: '核心+功能', subtitle: '核心稳定性训练', durationMin: 30, intensity: '低' as const, note: '居家版' },
    { dayOfWeek: 7, type: 'rest' as const, label: '休息日', subtitle: '完全休息', durationMin: 0 },
  ];
}

// ========== 有氧配置辅助函数 ==========

function cardioByGoal(goal: 'fat-loss' | 'muscle-gain' | 'shape', intensity: '低' | '中' | '高'): string {
  if (goal === 'fat-loss') {
    if (intensity === '高') return '间歇跑 / 跳绳';
    if (intensity === '中') return '慢跑 / 单车 / 爬楼';
    return '快走 / 爬坡 / 长距离散步';
  }
  if (goal === 'muscle-gain') {
    return '低强度有氧 · 活动恢复';
  }
  // shape
  if (intensity === '高') return '爬楼 / 跳绳';
  if (intensity === '中') return '平路爬坡 / 单车';
  return '快走 / 散步';
}

function cardioMinByGoal(goal: 'fat-loss' | 'muscle-gain' | 'shape', len: 'short' | 'mid' | 'long'): number {
  if (goal === 'muscle-gain') return len === 'short' ? 20 : len === 'mid' ? 30 : 30;
  if (goal === 'fat-loss') return len === 'short' ? 30 : len === 'mid' ? 40 : 45;
  return len === 'short' ? 30 : len === 'mid' ? 35 : 40; // shape
}

// ========== 碳日联动 ==========
/**
 * 碳日类型严格对应训练日类型：
 * - 力量日 → 高碳
 * - 有氧日/HIIT → 中碳（过渡日）
 * - 休息日/主动恢复 → 低碳
 */
export function getCarbDayFromTrainingDay(dayType: TrainingDayType): 'high' | 'medium' | 'low' {
  if (dayType === 'strength') return 'high';
  if (dayType === 'cardio' || dayType === 'hiit') return 'medium';
  return 'low'; // rest / active-rest
}

export function carbDayLabel(type: 'high' | 'medium' | 'low') {
  return type === 'high' ? '高碳日' : type === 'medium' ? '中碳日' : '低碳日';
}

export function carbDaySubLabel(dayType: TrainingDayType): string {
  if (dayType === 'strength') return '力量训练日';
  if (dayType === 'cardio' || dayType === 'hiit') return '有氧过渡日';
  if (dayType === 'active-rest') return '主动恢复';
  return '休息日';
}

// ========== 主生成函数 ==========

/**
 * 根据伤病部位过滤动作 ID 列表
 * 命中以下任一条件则被移除：
 * 1. 动作 bodyPart 在伤病部位的规避 bodyParts 列表中
 * 2. 动作 subCategory 在伤病部位的规避 subCategories 列表中
 * 3. 动作名称包含关键词（中文名称匹配）
 */
function filterExercisesByInjury(exerciseIds: string[], injuryParts: InjuryPart[]): { filtered: string[]; removed: string[] } {
  if (injuryParts.length === 0) return { filtered: exerciseIds, removed: [] };

  // 构建全部规避规则
  const avoidBodyParts = new Set<string>();
  const avoidSubCats = new Set<string>();
  const avoidKeywords: string[] = [];
  for (const part of injuryParts) {
    const rule = INJURY_AVOID_MAP[part];
    if (!rule) continue;
    rule.bodyParts?.forEach(p => avoidBodyParts.add(p));
    rule.subCategories?.forEach(s => avoidSubCats.add(s));
    rule.keywords?.forEach(k => avoidKeywords.push(k));
  }

  const filtered: string[] = [];
  const removed: string[] = [];
  for (const id of exerciseIds) {
    const ex = MOCK_EXERCISES.find(e => e.id === id);
    if (!ex) {
      filtered.push(id);
      continue;
    }
    let shouldAvoid = false;
    if (avoidBodyParts.has(ex.bodyPart)) shouldAvoid = true;
    if (avoidSubCats.has(ex.subCategory)) shouldAvoid = true;
    if (avoidKeywords.some(kw => ex.name.includes(kw))) shouldAvoid = true;
    if (shouldAvoid) {
      removed.push(id);
    } else {
      filtered.push(id);
    }
  }
  return { filtered, removed };
}

export function generateTrainingPlan(input: GenerateInput): ITrainingPlan {
  const { goal, dietTemplate, weeklyHours, trainingPlace, trainingMonths, trainingFrequency, injuryParts = [] } = input;
  const system = matchCourseSystem(input);
  const courseObj = findCourseBySystem(system);
  const sessionMinutes = getTrainingSessionMinutes(weeklyHours, trainingFrequency, input.sessionMinutes);

  // 按频率+目标+场地生成周计划框架（原始顺序，力量日在最前）
  const baseDays = buildWeekDays({
    system,
    course: courseObj,
    frequency: trainingFrequency,
    goal,
    trainingPlace,
    trainingMonths,
  });

  // 提取原始力量日、有氧日、休息日
  const strengthBase = baseDays.filter(d => d.type === 'strength');
  const cardioBase = baseDays.filter(d => d.type === 'cardio' || d.type === 'hiit');
  const restBase = baseDays.filter(d => d.type === 'rest' || d.type === 'active-rest');

  // 用户勾选的力量训练日（1=周一 ... 7=周日）；未勾选则按原始 baseDays 的 dayOfWeek
  const userStrengthDays = input.strengthDays?.length ? input.strengthDays : strengthBase.map(d => d.dayOfWeek);
  const strengthCount = Math.min(userStrengthDays.length, strengthBase.length);
  const usedStrengthDays = userStrengthDays.slice(0, strengthCount).sort((a, b) => a - b);

  // 目标对应的每周有氧次数建议
  const targetCardioCount = goal === 'fat-loss' ? 3 : goal === 'shape' ? 3 : 2;

  // 计算可用放有氧的天：力量日次日优先 + 其余非力量日
  const allDays = [1, 2, 3, 4, 5, 6, 7];
  const strengthSet = new Set(usedStrengthDays);
  const nonStrengthDays = allDays.filter(d => !strengthSet.has(d));

  // 力量日次日（若不是力量日且不是周日也不是周一隔周情况）优先放有氧
  const nextDayOfStrength = usedStrengthDays
    .map(d => d === 7 ? 1 : d + 1)
    .filter(d => !strengthSet.has(d));
  // 去重并按顺序
  const priorityCardioDays = Array.from(new Set(nextDayOfStrength)).sort((a, b) => a - b);
  const otherDays = nonStrengthDays.filter(d => !priorityCardioDays.includes(d));
  const cardioDayCandidates = [...priorityCardioDays, ...otherDays];

  const cardioDayCount = Math.min(targetCardioCount, cardioDayCandidates.length, cardioBase.length);
  const cardioDays = cardioDayCandidates.slice(0, cardioDayCount);
  const cardioSet = new Set(cardioDays);

  // 其余天为休息日

  // 组装 7 天周计划，dayOfWeek 1-7 顺序
  const strengthCourses = strengthBase.slice(0, usedStrengthDays.length);
  const finalWeek: Omit<ITrainingPlanDay, 'courseId' | 'courseDayId' | 'exerciseIds' | 'warmupIds' | 'stretchIds'>[] = [];
  let sIdx = 0;
  let cIdx = 0;
  let rIdx = 0;

  for (let dow = 1; dow <= 7; dow++) {
    if (strengthSet.has(dow) && sIdx < strengthCourses.length) {
      finalWeek.push({ ...strengthCourses[sIdx], dayOfWeek: dow });
      sIdx += 1;
    } else if (cardioSet.has(dow) && cIdx < cardioBase.length) {
      finalWeek.push({ ...cardioBase[cIdx], dayOfWeek: dow });
      cIdx += 1;
    } else {
      // 休息日或主动恢复（取一个 restBase 兜底）
      const restDay = restBase[rIdx % Math.max(1, restBase.length)] || {
        dayOfWeek: dow,
        type: 'rest' as const,
        label: '休息日',
        subtitle: '完全休息',
        durationMin: 0,
        intensity: '低' as const,
      };
      finalWeek.push({ ...restDay, dayOfWeek: dow });
      rIdx += 1;
    }
  }

  // 将力量日与具体课程日绑定；居家时做动作映射；伤病时过滤规避动作
  let strengthCount2 = 0;
  const weekDays: ITrainingPlanDay[] = finalWeek.map((day) => {
    if (day.type === 'strength') {
      strengthCount2 += 1;
      const selectedDay = trainingPlace === 'home' ? getHomeTrainingDay(strengthCount2, trainingFrequency, sessionMinutes)
        : selectGymTrainingDay(system, day.label, strengthCount2);
      const courseDay = selectedDay.day;
      let mainIds = courseDay.mainIds;
      let warmupIds = courseDay.warmupIds;
      let stretchIds = courseDay.stretchIds;
      let injuryNote = '';
      // 伤病：过滤主动作中的规避动作（热身和拉伸保留，除非涉及该部位）
      if (injuryParts.length > 0) {
        const mainResult = filterExercisesByInjury(mainIds, injuryParts);
        mainIds = mainResult.filtered;
        if (mainResult.removed.length > 0) {
          injuryNote = `· 已避开${injuryParts.length}处不适动作（替换${mainResult.removed.length}个）`;
        }
      }
      // 塑形目标追加体态专项提示
      const shapeNote = goal === 'shape' ? '· 含体态专项' : '';
      const finalNote = [day.note, shapeNote, injuryNote].filter(Boolean).join(' ');
      return {
        ...day,
        courseSystem: system,
        durationMin: sessionMinutes,
        intensity: trainingPlace === 'home' ? getTrainingExperienceSettings(trainingMonths).intensity : day.intensity,
        courseId: selectedDay.course.id,
        courseDayId: courseDay.id,
        exerciseIds: mainIds,
        warmupIds,
        stretchIds,
        note: finalNote || undefined,
      };
    }
    if (day.type === 'cardio' || day.type === 'hiit') {
      return {
        ...day,
        cardioItem: day.subtitle,
      };
    }
    return day as ITrainingPlanDay;
  });

  const systemName = {
    personal: '私教跟练',
    'three-way': '三分化',
    'four-way': '四分化',
    'ksw-collection': '凯圣王合集',
    home: '居家训练',
    fanjue: '晋升突破',
    'fanjue-plan': '焚诀计划',
  }[system] || '训练计划';

  const goalName = { 'fat-loss': '减脂', 'muscle-gain': '增肌', shape: '塑形' }[goal];
  const venueName = trainingPlace === 'gym' ? '健身房' : '居家';
  const freqLabel = { '1-2': '1-2练/周', '3-4': '3-4练/周', '5-6': '5-6练/周' }[trainingFrequency];

  const now = Date.now();
  // 计划开始日期：用户指定 → 今天
  let startTs: number;
  if (input.startDate) {
    startTs = input.startDate;
  } else {
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    startTs = today.getTime();
  }
  const startDateObj = new Date(startTs);
  startDateObj.setHours(0, 0, 0, 0);
  const startDate = startDateObj.getTime();

  return {
    id: 'tp_' + now.toString(36) + Math.random().toString(36).slice(2, 6),
    name: `${goalName}·${systemName}${venueName}版（${freqLabel}）`,
    dietTemplate,
    courseSystem: system,
    goal,
    weeklyHours,
    sessionMinutes,
    trainingPlace,
    trainingMonths,
    trainingFrequency,
    startDate,
    scheduleMode: 'calendar',
    weekDays,
    createdAt: now,
  };
}
